/**
 * S-58 統括表・明細表（F-EXP-06）。kintone: 出力指示（監視事業作業費用統括表作成）
 * 出力条件で集計（プレビュー）し、別記1（統括表 R-14）・別記2（明細表 R-15）を Excel 出力する。
 */
import { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Eye, FileSpreadsheet, Table2 } from 'lucide-react';
import { Button, DateField, EmptyState, FormField, PageHeader, Panel, RadioGroup, Select, TextField, useToast } from '@/components';
import { useFiscalYear, useFiscalYears } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { cx } from '@/lib/cx';
import { fiscalYearLabel } from '@/lib/wareki';
import { Money, Num, OperatorSelect, SummaryStrip, VesselExcludePicker } from '../common/components';
import { useOperatorScope } from '../common/useOperatorScope';
import {
  ORDER1,
  ORDER2,
  PATTERNS,
  PAYMENT_STATUSES,
  QUARTERS,
  SUBTITLES,
  TAX_ORDERS,
  useExportCost,
  usePreviewCost,
  type CostOptions,
  type CostRow,
  type CostTotals,
} from './api';

const radios = <T extends string>(list: readonly T[]) => list.map((v) => ({ value: v, label: v }));

export function CostSummaryPage() {
  const { year } = useFiscalYear();
  const years = useFiscalYears();
  const toast = useToast();
  const { isCoop, fixedOperatorId } = useOperatorScope();
  const preview = usePreviewCost();
  const exporter = useExportCost();
  const { control, register, handleSubmit, watch } = useForm<CostOptions>({
    defaultValues: {
      fiscalYear: year,
      businessOperatorId: fixedOperatorId,
      subtitle: '（概算払）',
      departureFrom: '',
      departureTo: '',
      surveyFrom: '',
      surveyTo: '',
      quarter: '選択無し',
      scheduledFrom: '',
      scheduledTo: '',
      paidFrom: '',
      paidTo: '',
      paymentStatus: '両方',
      pattern: '出港毎',
      order1: '参加船名簿、出港日の順',
      order2: '消費税対象を分けない',
      taxOrder: '有り→無しの順',
      excludeVesselIds: [],
    },
  });
  const quarter = watch('quarter');
  const order2 = watch('order2');
  const opYear = Number(watch('fiscalYear'));

  const validate = (o: CostOptions): boolean => {
    if (!isCoop && o.businessOperatorId == null) {
      toast.error('事業実施者名は必須です。');
      return false;
    }
    if (o.quarter === '選択無し' && !o.surveyFrom && !o.surveyTo) {
      toast.error('調査日の値か4半期のいずれかを選択してください');
      return false;
    }
    return true;
  };

  const onPreview = handleSubmit(async (o) => {
    if (!validate(o)) return;
    try {
      await preview.mutateAsync({ ...o, fiscalYear: Number(o.fiscalYear) });
    } catch (e) {
      toast.error('集計できませんでした', errorMessage(e));
    }
  });

  const onExport = (form: '別記1' | '別記2') =>
    handleSubmit(async (o) => {
      if (!validate(o)) return;
      try {
        const name = await exporter.mutateAsync({ options: { ...o, fiscalYear: Number(o.fiscalYear) }, form });
        toast.success('帳票を出力しました', name);
      } catch (e) {
        toast.error('帳票を出力できませんでした', errorMessage(e));
      }
    })();

  const r = preview.data;

  return (
    <>
      <PageHeader
        eyebrow="各種様式"
        title="統括表・明細表"
        description="監視事業作業費用の統括表（別記1）・明細表（別記2）を、出力条件で集計して Excel に出力します。"
        actions={
          <>
            <Button icon={<Eye size={16} />} onClick={() => void onPreview()} loading={preview.isPending}>
              集計（プレビュー）
            </Button>
            <Button icon={<FileSpreadsheet size={16} />} onClick={() => void onExport('別記1')} loading={exporter.isPending && exporter.variables?.form === '別記1'}>
              帳票出力（統括表・別記1）
            </Button>
            <Button variant="primary" icon={<Table2 size={16} />} onClick={() => void onExport('別記2')} loading={exporter.isPending && exporter.variables?.form === '別記2'}>
              帳票出力（明細表・別記2）
            </Button>
          </>
        }
      />

      <Panel title="出力条件">
        <div className="bs-exp-stack">
          <div className="bs-exp-options">
            <FormField label="事業実施者" required htmlFor="cost-operator">
              <Controller control={control} name="businessOperatorId" render={({ field }) => <OperatorSelect id="cost-operator" year={opYear} value={field.value} onChange={field.onChange} />} />
            </FormField>
            <FormField label="出力対象年度" required>
              <Select {...register('fiscalYear')} options={(years.data ?? [{ year, label: '' }]).map((y) => ({ value: String(y.year), label: fiscalYearLabel(y.year) }))} />
            </FormField>
            <FormField label="サブタイトル" hint="候補から選択または手入力">
              <TextField list="cost-subtitles" {...register('subtitle')} />
            </FormField>
            <datalist id="cost-subtitles">
              {SUBTITLES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <FormField label="出港日" span={2}>
              <span className="bs-exp-range">
                <DateField {...register('departureFrom')} aria-label="出港日FROM" />
                <span>〜</span>
                <DateField {...register('departureTo')} aria-label="出港日TO" />
              </span>
            </FormField>
            <FormField label="四半期" span={2}>
              <Controller control={control} name="quarter" render={({ field }) => <RadioGroup name="quarter" value={field.value} onChange={field.onChange} options={radios(QUARTERS)} />} />
            </FormField>
            <FormField label="調査日" span={2} hint={quarter === '選択無し' ? '四半期「選択無し」の場合は必須' : '四半期の期間で集計します'}>
              <span className="bs-exp-range">
                <DateField {...register('surveyFrom')} aria-label="調査日FROM" disabled={quarter !== '選択無し'} />
                <span>〜</span>
                <DateField {...register('surveyTo')} aria-label="調査日TO" disabled={quarter !== '選択無し'} />
              </span>
            </FormField>
            <FormField label="助成金支払予定日" span={2}>
              <span className="bs-exp-range">
                <DateField {...register('scheduledFrom')} aria-label="支払予定日FROM" />
                <span>〜</span>
                <DateField {...register('scheduledTo')} aria-label="支払予定日TO" />
              </span>
            </FormField>
            <FormField label="助成金支払日" span={2}>
              <span className="bs-exp-range">
                <DateField {...register('paidFrom')} aria-label="支払日FROM" />
                <span>〜</span>
                <DateField {...register('paidTo')} aria-label="支払日TO" />
              </span>
            </FormField>
            <FormField label="支払状況">
              <Controller control={control} name="paymentStatus" render={({ field }) => <RadioGroup name="paymentStatus" value={field.value} onChange={field.onChange} options={radios(PAYMENT_STATUSES)} />} />
            </FormField>
            <FormField label="出力パターン">
              <Controller control={control} name="pattern" render={({ field }) => <RadioGroup name="pattern" value={field.value} onChange={field.onChange} options={radios(PATTERNS)} />} />
            </FormField>
            <FormField label="並び順" span={2}>
              <Controller control={control} name="order1" render={({ field }) => <RadioGroup name="order1" value={field.value} onChange={field.onChange} options={radios(ORDER1)} />} />
            </FormField>
            <FormField label="消費税区分" span={2}>
              <Controller control={control} name="order2" render={({ field }) => <RadioGroup name="order2" value={field.value} onChange={field.onChange} options={radios(ORDER2)} />} />
            </FormField>
            <FormField label="消費税順番" span={2}>
              <Controller
                control={control}
                name="taxOrder"
                render={({ field }) => <RadioGroup name="taxOrder" value={field.value} onChange={field.onChange} options={radios(TAX_ORDERS)} disabled={order2 !== '消費税対象を分ける'} />}
              />
            </FormField>
          </div>
          <Controller
            control={control}
            name="excludeVesselIds"
            render={({ field }) => <VesselExcludePicker year={opYear} operatorId={isCoop ? fixedOperatorId : watch('businessOperatorId')} value={field.value} onChange={field.onChange} />}
          />
        </div>
      </Panel>

      {!r ? (
        <EmptyState icon={<Table2 />} title="出力条件を指定して「集計（プレビュー）」を押してください" description="「帳票出力（統括表・別記1）」「帳票出力（明細表・別記2）」で Excel ファイルを出力します（プレビューを押さずに出力することもできます）。" />
      ) : r.groups.length === 0 ? (
        <EmptyState icon={<Table2 />} title="出力対象データがありません！" description="条件を変更してください。" />
      ) : (
        <div className="bs-exp-stack">
          <SummaryStrip
            items={[
              { label: '①賃金', value: <Money value={r.summary.wageAmount} /> },
              { label: '人日', value: <Num value={r.summary.manDays} /> },
              { label: '②用船料 15トン未満', value: <Money value={r.summary.charter['１５トン未満']?.amount ?? 0} /> },
              { label: '②用船料 15〜30トン', value: <Money value={r.summary.charter['１５トン以上３０トン以下']?.amount ?? 0} /> },
              { label: '③燃料費', value: <Money value={r.summary.fuelAmount} /> },
              { label: '④消耗品費', value: <Money value={r.summary.consumablesAmount} /> },
              { label: '⑧助成対象消費税額', value: <Money value={r.summary.targetAmount} /> },
              { label: '計', value: <Money value={r.summary.total} />, emphasis: true },
            ]}
          />
          {(r.summary.charter['３１トン以上']?.amount ?? 0) > 0 && (
            <p className="bs-exp-note">31 トン以上の用船料（{(r.summary.charter['３１トン以上']?.amount ?? 0).toLocaleString()} 円）は別記1 の様式に記入欄がないため、統括表には含まれません（明細表には出力されます）。</p>
          )}
          <Panel title={`明細表プレビュー（調査日 ${r.conditions.surveyFrom}〜${r.conditions.surveyTo}）`} flush>
            <div className="bs-exp-sheet">
              <table aria-label="明細表プレビュー">
                <thead>
                  <tr>
                    <th rowSpan={2}>No</th>
                    <th rowSpan={2}>船名</th>
                    <th rowSpan={2}>漁業種類</th>
                    <th rowSpan={2}>頓数</th>
                    <th rowSpan={2}>馬力</th>
                    <th rowSpan={2}>作業開始日</th>
                    <th rowSpan={2}>作業終了日</th>
                    <th rowSpan={2}>作業海域</th>
                    <th rowSpan={2}>乗船人員</th>
                    <th rowSpan={2}>作業日数</th>
                    <th rowSpan={2}>作業人日</th>
                    <th rowSpan={2}>賃金</th>
                    <th rowSpan={2}>用船料</th>
                    <th rowSpan={2}>燃油代</th>
                    <th rowSpan={2}>潤滑油代</th>
                    <th colSpan={2}>消費税</th>
                    <th rowSpan={2}>合計</th>
                    <th rowSpan={2}>備考</th>
                    <th rowSpan={2}>日平均</th>
                    <th rowSpan={2}>給油日数</th>
                  </tr>
                  <tr>
                    <th>対象率</th>
                    <th>対象額</th>
                  </tr>
                </thead>
                <tbody>
                  {r.groups.map((g) => (
                    <Fragment key={g.key}>
                      <tr className={cx('is-category', g.taxGroup === '有り' && 'is-tax-yes', g.taxGroup === '無し' && 'is-tax-no')}>
                        <td colSpan={21}>{g.label}</td>
                      </tr>
                      {g.rows.map((row, i) => (
                        <CostRowView key={`${g.key}-${i}`} row={row} />
                      ))}
                      <TotalsRow label="小計" t={g.subtotal} className="is-subtotal" />
                    </Fragment>
                  ))}
                  {r.middles.map((m) => (
                    <TotalsRow key={m.label} label={m.label} t={m.totals} className="is-middle" />
                  ))}
                  <TotalsRow label="合計" t={r.total} className="is-total" />
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}

function CostRowView({ row }: { row: CostRow }) {
  return (
    <tr>
      <td className="is-center bs-num">{row.no ?? ''}</td>
      <td>{row.vesselName ?? ''}</td>
      <td>{row.fishingMethods ?? ''}</td>
      <td className="is-right"><Num value={row.tonnage} /></td>
      <td className="is-center">{row.horsepower ?? ''}</td>
      <td className="is-center bs-num">{row.startDate}</td>
      <td className="is-center bs-num">{row.endDate}</td>
      <td className="bs-num" style={{ whiteSpace: 'normal', minWidth: 120 }}>{row.zones}</td>
      <td className="is-center bs-num">{row.crew}</td>
      <td className="is-right"><Num value={row.workDays} /></td>
      <td className="is-right"><Num value={row.manDays} /></td>
      <td className="is-right"><Money value={row.wage} /></td>
      <td className="is-right"><Money value={row.charter} /></td>
      <td className="is-right"><Money value={row.fuel} /></td>
      <td className="is-right"><Money value={row.lube} /></td>
      <td className="is-center bs-num">{row.targetRateLabel}</td>
      <td className="is-right"><Money value={row.targetAmount} /></td>
      <td className="is-right"><strong><Money value={row.total} /></strong></td>
      <td>{row.remarks}</td>
      <td className="is-right bs-num">{row.dailyAverage}</td>
      <td className="is-right bs-num">{row.fuelDays ?? ''}</td>
    </tr>
  );
}

function TotalsRow({ label, t, className }: { label: string; t: CostTotals; className: string }) {
  return (
    <tr className={className}>
      <td colSpan={8} className="is-center">{label}</td>
      <td className="is-center bs-num">{t.crew}</td>
      <td className="is-right"><Num value={t.workDays} /></td>
      <td className="is-right"><Num value={t.manDays} /></td>
      <td className="is-right"><Money value={t.wage} /></td>
      <td className="is-right"><Money value={t.charter} /></td>
      <td className="is-right"><Money value={t.fuel} /></td>
      <td className="is-right"><Money value={t.lube} /></td>
      <td />
      <td className="is-right"><Money value={t.targetAmount} /></td>
      <td className="is-right"><Money value={t.total} /></td>
      <td colSpan={3} />
    </tr>
  );
}
